import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import partnerApi from "./api";
import { abbrNum } from "$utils/number-helper";
import ProbabilityBubble from "$components/probability";
import "./partner-review.scss";

export const OpportunityCard = ({ row, onDecide, disabled: parentDisabled }) => {
    const [saving, setSaving] = useState(false);
    const currentStatus = row.decisionStatus;
    const isDisabled = saving || parentDisabled;

    const decide = async (status) => {
        setSaving(true);
        await onDecide(row.projectKey, status);
        setSaving(false);
    };

    const clientLabel = row.clientGroupName || row.clientName;
    const subClient =
        row.clientName && row.clientName !== row.clientGroupName
            ? row.clientName
            : null;

    return (
        <div className={`pr-card${currentStatus ? " pr-card--decided" : ""}`}>
            <div className="pr-card__top">
                <div className="pr-card__identity">
                    <span className="pr-card__client">{clientLabel}</span>
                    {subClient && (
                        <span className="pr-card__subclient">{subClient}</span>
                    )}
                    <span className="pr-card__project">{row.name || row.projectTypeGroup}</span>
                </div>
                {currentStatus && (
                    <span className={`pr-card__decision pr-card__decision--${currentStatus}`}>
                        {currentStatus === "pursue" ? "Pursue" : "Pass"}
                    </span>
                )}
            </div>

            <div className="pr-card__meta">
                {row.serviceLine && (
                    <div className="pr-meta-item">
                        <span className="pr-meta-item__label">Service Line</span>
                        <span className="pr-meta-item__value">{row.serviceLine}</span>
                    </div>
                )}
                {row.industry && (
                    <div className="pr-meta-item">
                        <span className="pr-meta-item__label">Industry</span>
                        <span className="pr-meta-item__value">{row.industry}</span>
                    </div>
                )}
                <div className="pr-meta-item">
                    <span className="pr-meta-item__label">Est. Revenue</span>
                    <span className="pr-meta-item__value pr-meta-item__value--currency">
                        <sup>$</sup>{abbrNum(row.averageRevenue)}
                    </span>
                </div>
                <div className="pr-meta-item">
                    <span className="pr-meta-item__label">Probability</span>
                    <ProbabilityBubble value={row.probability} />
                </div>
                {row.isCrossSell && (
                    <div className="pr-meta-item">
                        <span className="pr-meta-item__label">&nbsp;</span>
                        <span className="badge badge-info">Cross-sell</span>
                    </div>
                )}
                {row.requiresIndependence && (
                    <div className="pr-meta-item">
                        <span className="pr-meta-item__label">&nbsp;</span>
                        <span className="badge badge-warning">Independence check</span>
                    </div>
                )}
            </div>

            <div className="pr-card__actions">
                {!currentStatus && <span className="pr-sign-here" aria-hidden="true" />}
                <button
                    className={`button -small${currentStatus === "pursue" ? " -primary" : " -outline"}`}
                    disabled={isDisabled}
                    onClick={() => decide("pursue")}
                >
                    Pursue
                </button>
                <button
                    className={`button -small${currentStatus === "pass" ? " -secondary" : " -outline"}`}
                    disabled={isDisabled}
                    onClick={() => decide("pass")}
                >
                    Pass
                </button>
                {currentStatus && (
                    <button
                        className="button -text -small"
                        disabled={isDisabled}
                        onClick={() => decide(null)}
                    >
                        Undo
                    </button>
                )}
            </div>
        </div>
    );
};

const PartnerReview = () => {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("loading");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        partnerApi.getReview(token).then((res) => {
            if (!res) { setStatus("expired"); return; }
            setData(res);
            setRows(res.shortlist || []);
            setStatus("ready");
        });
    }, [token]);

    const handleDecide = useCallback((projectKey, decisionStatus) => {
        setRows((prev) => {
            const updated = prev.map((r) =>
                r.projectKey === projectKey ? { ...r, decisionStatus } : r
            );
            const allDone = updated.length > 0 && updated.every((r) => r.decisionStatus);
            if (allDone) {
                const decisions = updated
                    .filter((r) => r.decisionStatus)
                    .map((r) => ({ projectKey: r.projectKey, decisionStatus: r.decisionStatus, note: null }));
                setSubmitting(true);
                partnerApi.submitDecisions(token, decisions).then((ok) => {
                    if (ok) setStatus("submitted");
                    setSubmitting(false);
                });
            }
            return updated;
        });
    }, [token]);

    const inner = () => {
        if (status === "loading") return <div className="pr-state"><p>Loading…</p></div>;

        if (status === "expired") return (
            <div className="pr-state">
                <h2>Link Expired or Invalid</h2>
                <p>This review link has expired or is no longer valid. Please contact your BD representative for a new link.</p>
            </div>
        );

        if (status === "submitted") return (
            <div className="pr-state">
                <h2>Thank you!</h2>
                <p>Your decisions have been recorded. The team has been notified.</p>
            </div>
        );

        const undecided = rows.filter((r) => !r.decisionStatus);
        const decided   = rows.filter((r) => r.decisionStatus);
        const decidedCount = decided.length;

        return (
            <>
                <div className="pr-page__header">
                    <h1><em>Initiative</em> Review</h1>
                    <p>Hello {data?.partnerName} — please review the opportunities below and record your decisions.</p>
                </div>

                {rows.length === 0 ? (
                    <p className="empty-state">No opportunities are currently shortlisted for your review.</p>
                ) : (
                    <>
                        <p className="pr-progress">
                            {submitting ? "Saving your decisions…" : `${decidedCount} of ${rows.length} decided`}
                        </p>

                        {undecided.length > 0 && (
                            <div className="pr-section">
                                <h2 className="pr-section__title">Awaiting Your Decision ({undecided.length})</h2>
                                <div className="pr-list">
                                    {undecided.map((row) => (
                                        <OpportunityCard key={row.projectKey} row={row} onDecide={handleDecide} disabled={submitting} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {decided.length > 0 && (
                            <div className="pr-section">
                                <h2 className="pr-section__title">Decided ({decided.length})</h2>
                                <div className="pr-list">
                                    {decided.map((row) => (
                                        <OpportunityCard key={row.projectKey} row={row} onDecide={handleDecide} disabled={submitting} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <div className="pr-shell">
            <div className="pr-brand">
                <img src="/images/logo-orgaimi.svg" alt="Orgaimi" />
            </div>
            <div className="pr-page">
                {inner()}
            </div>
        </div>
    );
};

export default PartnerReview;
